/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useSystemOfUnits} from '@ion/applications/src/hooks/useSystemOfUnits';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Device} from '@ion/core/src/types/entities';
import {DeviceEditor, DeviceEditorOptions} from '@ion/dls/src/editors/DeviceEditor';
import {isOceanModeDevice, isSCITagDevice, isSelectedDeviceSCI} from '@ion/dls/src/editors/DeviceEditorUtils';
import {UserNotificationDialog} from '@ion/dls/src/notifications/UserNotificationDialog';
import {QueryKey} from '@ion/query/src/config/QueryKeys';
import {useDevice} from '@ion/query/src/hooks/useDevice';
import {removeQueries} from '@ion/query/src/utils/helpers';
import {deviceDetailSelector, devicesEditSelector} from '../../store/selectors/devices';
import {globalGroupSelector} from '../../store/selectors/global-group';
import {useAuthenticatedUser} from '../admin/users/useAuthenticatedUser';
import {editAdminDevice, editDevice} from './state';

export interface EditDeviceEditorProps extends DeviceEditorOptions {
  includeGroupProperties?: boolean;
  isViewDialog?: boolean;
  includeGroups?: boolean;
  usePuls?: boolean;
  includeSpreaderChooser?: boolean;
  isMultiSelectSpreader?: boolean;
  includeIdentityInformation?: boolean;
};

// Determines if the given device has an asset associated with it.
const hasAssetAssociation = (device: Device, originalAccount: string): boolean =>
  !!(device.asset?.href && device.account?.title !== originalAccount);

// Success callback that runs upon the successful edit of a device.
const onSuccess = ({id}: Device): void => {
  removeQueries(QueryKey.DEVICE_GET, false);
};

/**
 * Display View/Edit Device Editor
 */
export const EditDeviceEditor = ({
  usePuls = false,
  includeAdditionalDeviceFields = false,
  isDeviceNotes = false,
  includeGPSFields = false,
  includeDatetimeInformation = false,
  includeIdentityInformation = false,
  isViewDialog = false,
  includeGroups = false,
  showSCISettings = false,
  disableAccountChooser = false,
  includeSpreaderChooser = false,
  isMultiSelectSpreader = false,
  includeGroupProperties = true,
  showDeviceType = false
}: EditDeviceEditorProps) => {
  const dispatch                          = useDispatch();
  const globalGroup                       = useSelector(globalGroupSelector);
  const {account}                         = useAuthenticatedUser();
  const {
    result: editedDevice,
    loading
  }                                       = useSelector(devicesEditSelector);
  const deviceDetail                      = useSelector(deviceDetailSelector);
  const {
    device: deviceDetails,
    isFetching: deviceDetailsLoading
  }                                       = useDevice({
    device             : deviceDetail,
    id                 : deviceDetail?.id,
    includeSCISettings : (isSCITagDevice(deviceDetail) || isOceanModeDevice(deviceDetail) ||
      isSelectedDeviceSCI(deviceDetail)) as boolean
  });
  const [clearForm, setClearForm]         = useState(false);
  const [editConfirm, setEditConfirm]     = useState(false);
  const [updatedDevice, setUpdatedDevice] = useState({});
  const systemOfUnits                     = useSystemOfUnits();
  const assetTitle                        = deviceDetails?.asset?.title;
  const originalAccountTitle              = deviceDetails?.account?.title;

  useEffect(() => {
    if (editedDevice) {
      setClearForm(true);
      dispatch(editAdminDevice(null, false));
    }
  }, [editedDevice]);

  const onDeviceSave = useCallback((device: Device) => {
    if (hasAssetAssociation(device, originalAccountTitle) && !usePuls) {
      setUpdatedDevice(device);
      setEditConfirm(true);
    } else {
      const selectedGroupIds = device.groups?.map(group => entityId(group));
      const groupsToDelete   = deviceDetails?.groups?.filter((group) => !selectedGroupIds.includes(entityId(group)));
      dispatch(editDevice({
        usePulse       : usePuls,
        device         : device,
        callbackFn     : onSuccess,
        groupsToDelete : groupsToDelete
      }));
    }
  }, [deviceDetails, usePuls]);

  const onCancel = useCallback(() => {
    dispatch(editAdminDevice(null, false));
  }, []);

  const onDismiss = useCallback(() => {
    setEditConfirm(false);
  }, []);

  const onAction = useCallback(() => {
    dispatch(editDevice({
      device     : updatedDevice,
      usePulse   : usePuls,
      callbackFn : onSuccess
    }));
  }, [updatedDevice, usePuls]);

  return (
    <>
      <DeviceEditor open
        includeAdditionalDeviceFields={includeAdditionalDeviceFields}
        isDeviceNotes={isDeviceNotes}
        includeDatetimeInformation={includeDatetimeInformation}
        includeIdentityInformation={includeIdentityInformation}
        includeGPSFields={includeGPSFields}
        showDeviceType={showDeviceType}
        onSubmit={onDeviceSave}
        onCloseDialog={onCancel}
        systemOfUnits={systemOfUnits}
        account={globalGroup || account}
        device={deviceDetails}
        includeGroupProperties={includeGroupProperties}
        disableAccountChooser={disableAccountChooser}
        includeSpreaderChooser={includeSpreaderChooser}
        isMultiSelectSpreader={isMultiSelectSpreader}
        loading={loading || deviceDetailsLoading}
        showSCISettings={showSCISettings}
        includeGroups={includeGroups}
        isViewDialog={isViewDialog}
        clear={clearForm} />
      {editConfirm && (
        <UserNotificationDialog onBackdropClick={onDismiss}
          onDismiss={onDismiss}
          onAction={onAction}
          title={translate('common:editors.deviceEditor.confirmTitle')}
          message={`${translate('common:editors.deviceEditor.confirmMessage', {asset: assetTitle})}`}
          actionLabel={translate('common:editors.shared.continue')}
          dismissLabel={translate('common:editors.shared.cancel')}
          show />
      )}
    </>
  );
};

export default memo(EditDeviceEditor);
